/**
 * Notification settings domain types.
 *
 * `GET/PATCH /events/api/v1/settings/notifications/` returns both the routing
 * matrices (flat `code → {email,whatsapp,sms}` maps) AND a `catalog` block with
 * each code's label / subtitle / group. The mapper
 * (`actions/ssr/settings/notificationApi.ts`) joins the catalog onto the matrix
 * by `code` to build the grouped UI. The delivery channels (the grid columns)
 * also come from the API `channels` block (label + availability) — the FE no
 * longer hardcodes either the catalogue or the channel list.
 */

export type NotificationChannel = "email" | "whatsapp" | "sms";
export type NotificationAudience = "customer" | "staff";

/** Per-channel on/off for a single notification (or the global channel row). */
export interface ChannelFlags {
  email: boolean;
  whatsapp: boolean;
  sms: boolean;
}

/** A single toggleable notification and its per-channel delivery flags. */
export interface NotificationToggle {
  /** API notification code — the stable identifier sent on PATCH. */
  key: string;
  label: string;
  description?: string;
  channels: ChannelFlags;
}

/** A titled block of notifications (a table in the UI). */
export interface NotificationGroup {
  title: string;
  items: NotificationToggle[];
}

/**
 * One delivery channel from the API `channels` block — the columns of the grid.
 * `available` is a platform/release fact (is sending wired at all), distinct from
 * the per-tenant on/off in `NotificationSettings.channels`.
 */
export interface NotificationChannelMeta {
  channel: NotificationChannel;
  label: string;
  /** `false` → render disabled + "coming soon" regardless of the tenant toggle. */
  available: boolean;
  /** Server-owned supporting copy under the label. `""` → render nothing. */
  subtitle: string;
}

export interface NotificationSettings {
  /** Which channels are enabled tenant-wide (per-channel on/off). */
  channels: ChannelFlags;
  /** The channel catalogue (labels + availability) from the API `channels` block. */
  channelList: NotificationChannelMeta[];
  senderName: string;
  replyToEmail: string;
  /** Customer-facing notifications, grouped. */
  customer: NotificationGroup[];
  /** Staff-facing notifications, grouped. */
  staff: NotificationGroup[];
}

/**
 * Fallback used only when the read fails. The notification catalogue (labels /
 * subtitles / groups) is served by the API `catalog` block, so on failure the
 * matrices are empty — there's no hardcoded catalogue to fall back to.
 */
export function defaultNotificationSettings(): NotificationSettings {
  return {
    channels: { email: true, whatsapp: false, sms: false },
    channelList: [],
    senderName: "",
    replyToEmail: "",
    customer: [],
    staff: [],
  };
}
